import { useEffect, useRef } from "react";
import type {
	Control,
	FieldValues,
	Path,
	PathValue,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from "react-hook-form";

import ComboBox from "~/components/formElements/ComboBox";
import FormInputError from "~/components/formElements/FormInputError";
import BillingSettingsProjectValue
	from "~/modules/billing/components/formSections/components/BillingSettingsProjectValue.tsx";
import type { BillingSettingsDivergingFields } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { FormInputOption } from "~/types/form.ts";

type Props<T extends FieldValues> = {
	control: Control<T>;
	errorMessage?: string;
	fieldsDivergingFromProject?: BillingSettingsDivergingFields;
	invoiceRecipientSelectOptions: FormInputOption[];
	isSubmitted?: boolean;
	clientContactPersonSelectOptions: FormInputOption[];
	setValue: UseFormSetValue<T>;
	trigger: UseFormTrigger<T>;
	watch: UseFormWatch<T>;
};

interface InvoiceRecipientFormSectionFormData {
	invoiceRecipientId: string | null;
	clientContactPersonId: string | null;
}

const InvoiceRecipientFormSection = <T extends InvoiceRecipientFormSectionFormData>({
	control,
	errorMessage,
	fieldsDivergingFromProject,
	invoiceRecipientSelectOptions,
	isSubmitted,
	clientContactPersonSelectOptions,
	setValue,
	trigger,
	watch,
}: Props<T>) => {
	const invoiceRecipientId = watch("invoiceRecipientId" as Path<T>);
	const clientContactPersonId = watch("clientContactPersonId" as Path<T>);
	const previousInvoiceRecipientId = useRef(invoiceRecipientId);
	const previousClientContactPersonId = useRef(clientContactPersonId);

	useEffect(() => {
		const hasChanged = invoiceRecipientId !== previousInvoiceRecipientId.current;
		previousInvoiceRecipientId.current = invoiceRecipientId;

		if (hasChanged && invoiceRecipientId) {
			setValue("clientContactPersonId" as Path<T>, null as PathValue<T, any>);
			if (isSubmitted === undefined || isSubmitted) {
				trigger();
			}
		}
	}, [invoiceRecipientId, isSubmitted, setValue, trigger]);

	useEffect(() => {
		const hasChanged = clientContactPersonId !== previousClientContactPersonId.current;
		previousClientContactPersonId.current = clientContactPersonId;

		if (hasChanged && clientContactPersonId) {
			setValue("invoiceRecipientId" as Path<T>, null as PathValue<T, any>);
			if (isSubmitted === undefined || isSubmitted) {
				trigger();
			}
		}
	}, [clientContactPersonId, isSubmitted, setValue, trigger]);

	return (
		<div className="grid grid-cols-6 gap-x-6 gap-y-2">
			<div className="col-span-6">
				<ComboBox
					allowNew={false}
					disabled={invoiceRecipientSelectOptions.length === 0}
					placeholder={invoiceRecipientSelectOptions.length === 0 ? "Keine Rechnungsempfänger verfügbar" : undefined}
					control={control}
					label="Zentraler Rechnungsempfänger"
					name="invoiceRecipientId"
					optionsData={invoiceRecipientSelectOptions}
				/>
			</div>
			<div className="col-span-6">
				<ComboBox
					allowNew={false}
					control={control}
					label="Kontaktperson"
					name="clientContactPersonId"
					optionsData={clientContactPersonSelectOptions}
				/>
			</div>
			{fieldsDivergingFromProject?.resolvedInvoiceRecipient.isDifferent && fieldsDivergingFromProject?.resolvedInvoiceRecipient.displayValue?.displayName &&
				<BillingSettingsProjectValue className="col-span-6"
											 value={`${fieldsDivergingFromProject?.resolvedInvoiceRecipient.displayValue?.displayName} (${fieldsDivergingFromProject?.resolvedInvoiceRecipient.displayValue?.typeDisplayName})`} />}
			<div className={"col-span-6"}>{errorMessage && <FormInputError>{errorMessage}</FormInputError>}</div>
		</div>

	);
};

export default InvoiceRecipientFormSection;